import { ChatMessage, ChatPanelOptions } from '../types';
import { CHAR_COUNT_THRESHOLD, MAX_INPUT_LENGTH } from '../constants';
import { renderMarkdown } from '../util/markdown-renderer';

/**
 * ChatPanel renders the overlay chat dialog with message thread,
 * input area, and header. Manages DOM updates for messages,
 * loading state, and error display.
 *
 * Uses BEM naming convention with `chatbot-panel` prefix.
 */
export class ChatPanel {
  private container: HTMLElement;
  private panel: HTMLDivElement;
  private header: HTMLDivElement;
  private closeButton: HTMLButtonElement;
  private messagesArea: HTMLDivElement;
  private form: HTMLFormElement;
  private textarea: HTMLTextAreaElement;
  private charCounter: HTMLSpanElement;
  private sendButton: HTMLButtonElement;
  private options: ChatPanelOptions;
  private panelOpen = false;

  private handleKeyDown: (event: KeyboardEvent) => void;
  private handleInput: () => void;
  private handlePaste: (event: ClipboardEvent) => void;
  private handleSubmit: (event: Event) => void;
  private handleTextareaKeyDown: (event: KeyboardEvent) => void;
  private handleCloseClick: () => void;

  constructor(container: HTMLElement, options: ChatPanelOptions) {
    this.container = container;
    this.options = options;

    // Create panel container
    this.panel = document.createElement('div');
    this.panel.className = 'chatbot-panel';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', options.ariaLabel);
    this.panel.setAttribute('hidden', '');

    // Header
    this.header = document.createElement('div');
    this.header.className = 'chatbot-panel__header';

    const title = document.createElement('h2');
    title.className = 'chatbot-panel__title';
    title.textContent = options.title;

    this.closeButton = document.createElement('button');
    this.closeButton.className = 'chatbot-panel__close';
    this.closeButton.setAttribute('aria-label', 'Close chat');
    this.closeButton.setAttribute('type', 'button');
    this.closeButton.textContent = '\u00D7';

    this.header.appendChild(title);
    this.header.appendChild(this.closeButton);

    // Message area
    this.messagesArea = document.createElement('div');
    this.messagesArea.className = 'chatbot-panel__messages';
    this.messagesArea.setAttribute('role', 'log');
    this.messagesArea.setAttribute('aria-live', 'polite');
    this.messagesArea.setAttribute('aria-atomic', 'false');

    // Input form
    this.form = document.createElement('form');
    this.form.className = 'chatbot-panel__input-area';
    this.form.setAttribute('role', 'form');

    this.textarea = document.createElement('textarea');
    this.textarea.className = 'chatbot-panel__input';
    this.textarea.setAttribute('aria-label', 'Type your message');
    this.textarea.setAttribute('maxlength', String(MAX_INPUT_LENGTH));
    this.textarea.setAttribute('rows', '1');

    this.charCounter = document.createElement('span');
    this.charCounter.className = 'chatbot-panel__char-count';
    this.charCounter.setAttribute('aria-live', 'polite');
    this.charCounter.setAttribute('hidden', '');

    this.sendButton = document.createElement('button');
    this.sendButton.className = 'chatbot-panel__send';
    this.sendButton.setAttribute('type', 'submit');
    this.sendButton.setAttribute('aria-label', 'Send message');
    this.sendButton.disabled = true;
    this.sendButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';

    this.form.appendChild(this.textarea);
    this.form.appendChild(this.charCounter);
    this.form.appendChild(this.sendButton);

    // Assemble panel
    this.panel.appendChild(this.header);
    this.panel.appendChild(this.messagesArea);
    this.panel.appendChild(this.form);

    // Attach to container
    this.container.appendChild(this.panel);

    // Bind event handlers
    this.handleCloseClick = () => {
      this.options.onClose();
    };

    this.handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.options.onClose();
      }
    };

    this.handleInput = () => {
      this.updateSendButtonState();
      this.updateCharCounter();
    };

    this.handlePaste = (event: ClipboardEvent) => {
      const pastedText = event.clipboardData?.getData('text') ?? '';
      const currentValue = this.textarea.value;
      const selectionStart = this.textarea.selectionStart ?? currentValue.length;
      const selectionEnd = this.textarea.selectionEnd ?? currentValue.length;

      // Calculate what the resulting text would be
      const before = currentValue.slice(0, selectionStart);
      const after = currentValue.slice(selectionEnd);
      const combined = before + pastedText + after;

      if (combined.length > MAX_INPUT_LENGTH) {
        event.preventDefault();
        // Truncate to fit within max length
        const allowedPasteLength = MAX_INPUT_LENGTH - before.length - after.length;
        const truncatedPaste = pastedText.slice(0, Math.max(0, allowedPasteLength));
        this.textarea.value = before + truncatedPaste + after;
        // Set cursor position after pasted text
        const cursorPos = before.length + truncatedPaste.length;
        this.textarea.setSelectionRange(cursorPos, cursorPos);
        this.updateSendButtonState();
        this.updateCharCounter();
      }
    };

    this.handleSubmit = (event: Event) => {
      event.preventDefault();
      this.submitMessage();
    };

    this.handleTextareaKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.submitMessage();
      }
    };

    // Attach listeners
    this.closeButton.addEventListener('click', this.handleCloseClick);
    this.panel.addEventListener('keydown', this.handleKeyDown);
    this.textarea.addEventListener('input', this.handleInput);
    this.textarea.addEventListener('paste', this.handlePaste);
    this.form.addEventListener('submit', this.handleSubmit);
    this.textarea.addEventListener('keydown', this.handleTextareaKeyDown);
  }

  /**
   * Shows the chat panel.
   */
  open(): void {
    this.panel.removeAttribute('hidden');
    this.panelOpen = true;
  }

  /**
   * Hides the chat panel.
   */
  close(): void {
    this.panel.setAttribute('hidden', '');
    this.panelOpen = false;
  }

  /**
   * Returns whether the panel is currently open.
   */
  isOpen(): boolean {
    return this.panelOpen;
  }

  /**
   * Appends a chat message to the message area with appropriate
   * sender label, alignment, and BEM modifier classes.
   */
  appendMessage(message: ChatMessage): void {
    const messageEl = document.createElement('div');
    messageEl.className = `chatbot-panel__message chatbot-panel__message--${message.role}`;

    const label = document.createElement('span');
    label.className = 'chatbot-panel__message-label';
    label.textContent = message.role === 'user' ? 'You' : 'Assistant';

    const content = document.createElement('div');
    content.className = 'chatbot-panel__message-content';

    if (message.role === 'assistant') {
      content.appendChild(renderMarkdown(message.content));
    } else {
      content.textContent = message.content;
    }

    messageEl.appendChild(label);
    messageEl.appendChild(content);
    this.messagesArea.appendChild(messageEl);

    this.scrollToBottom();
  }

  /**
   * Displays a typing indicator in the message area.
   */
  showLoading(): void {
    const loadingEl = document.createElement('div');
    loadingEl.className = 'chatbot-panel__loading';
    loadingEl.setAttribute('aria-label', 'Assistant is typing');

    const dots = document.createElement('span');
    dots.className = 'chatbot-panel__loading-dots';
    dots.textContent = '...';

    loadingEl.appendChild(dots);
    this.messagesArea.appendChild(loadingEl);

    this.scrollToBottom();
  }

  /**
   * Removes the typing indicator from the message area.
   */
  hideLoading(): void {
    const loadingEl = this.messagesArea.querySelector('.chatbot-panel__loading');
    if (loadingEl) {
      loadingEl.remove();
    }
  }

  /**
   * Renders an error-styled assistant message.
   */
  showError(message: string): void {
    const messageEl = document.createElement('div');
    messageEl.className =
      'chatbot-panel__message chatbot-panel__message--assistant chatbot-panel__message--error';

    const label = document.createElement('span');
    label.className = 'chatbot-panel__message-label';
    label.textContent = 'Assistant';

    const content = document.createElement('div');
    content.className = 'chatbot-panel__message-content';
    content.textContent = message;

    messageEl.appendChild(label);
    messageEl.appendChild(content);
    this.messagesArea.appendChild(messageEl);

    this.scrollToBottom();
  }

  /**
   * Displays a welcome/placeholder message in the message area.
   */
  showPlaceholder(): void {
    const placeholderEl = document.createElement('div');
    placeholderEl.className = 'chatbot-panel__placeholder';
    placeholderEl.textContent =
      'Ask me a question about our knowledge base and I\'ll do my best to help.';

    this.messagesArea.appendChild(placeholderEl);
  }

  /**
   * Empties the message area of all messages, loading indicators, and placeholders.
   */
  clearMessages(): void {
    this.messagesArea.innerHTML = '';
  }

  /**
   * Returns the current value of the textarea input.
   */
  getInputValue(): string {
    return this.textarea.value;
  }

  /**
   * Clears the textarea input and resets the send button and char counter state.
   */
  clearInput(): void {
    this.textarea.value = '';
    this.updateSendButtonState();
    this.updateCharCounter();
  }

  /**
   * Enables or disables the textarea and send button.
   */
  setInputEnabled(enabled: boolean): void {
    this.textarea.disabled = !enabled;
    this.sendButton.disabled = !enabled || this.textarea.value.trim().length === 0;
  }

  /**
   * Moves keyboard focus to the textarea input.
   */
  focusInput(): void {
    this.textarea.focus();
  }

  /**
   * Removes the panel from the DOM and cleans up all event listeners.
   */
  destroy(): void {
    this.closeButton.removeEventListener('click', this.handleCloseClick);
    this.panel.removeEventListener('keydown', this.handleKeyDown);
    this.textarea.removeEventListener('input', this.handleInput);
    this.textarea.removeEventListener('paste', this.handlePaste);
    this.form.removeEventListener('submit', this.handleSubmit);
    this.textarea.removeEventListener('keydown', this.handleTextareaKeyDown);
    this.panel.remove();
  }

  /**
   * Returns the panel HTMLElement (for focus trap attachment).
   */
  getElement(): HTMLDivElement {
    return this.panel;
  }

  // --- Private helpers ---

  private submitMessage(): void {
    const text = this.textarea.value.trim();
    if (text.length === 0 || this.sendButton.disabled) {
      return;
    }
    this.options.onSubmit(text);
  }

  private updateSendButtonState(): void {
    const hasContent = this.textarea.value.trim().length > 0;
    this.sendButton.disabled = !hasContent || this.textarea.disabled;
  }

  private updateCharCounter(): void {
    const length = this.textarea.value.length;
    const remaining = MAX_INPUT_LENGTH - length;

    if (length > CHAR_COUNT_THRESHOLD) {
      this.charCounter.textContent = `${remaining} characters remaining`;
      this.charCounter.removeAttribute('hidden');
    } else {
      this.charCounter.setAttribute('hidden', '');
    }
  }

  private scrollToBottom(): void {
    this.messagesArea.scrollTop = this.messagesArea.scrollHeight;
  }
}
